import Dexie, { type EntityTable } from 'dexie'
import { createBoard, type AlignmentPreset, type BoardState } from './board'
import type { Game, Player, Team } from './game'

type DraftMode = 'whiteboard' | 'play'
export type DraftRecord = { key: 'current' | DraftMode; board: BoardState; savedAt: number }
export type SavedDrawing = { id: string; title: string; board: BoardState; savedAt: number; deletedAt?: number }
export type SavedAlignmentPreset = AlignmentPreset & { updatedAt: number; deletedAt?: number }
export type SyncTombstone = { id: string; deletedAt: number }

class CoachDatabase extends Dexie {
  drafts!: EntityTable<DraftRecord, 'key'>
  drawings!: EntityTable<SavedDrawing, 'id'>
  players!: EntityTable<Player, 'id'>
  teams!: EntityTable<Team, 'id'>
  games!: EntityTable<Game, 'id'>
  alignmentPresets!: EntityTable<SavedAlignmentPreset, 'id'>
  syncTombstones!: EntityTable<SyncTombstone, 'id'>

  constructor() {
    super('basketball-coach')
    this.version(1).stores({
      drafts: 'key, savedAt',
      drawings: 'id, title, savedAt',
    })
    this.version(2).stores({ players: 'id, name', teams: 'id, name', games: 'id, teamId' })
    this.version(3).stores({ alignmentPresets: 'id, updatedAt' })
    this.version(4).stores({ syncTombstones: 'id, deletedAt' })
  }
}

export const db = new CoachDatabase()

export async function clearLocalData(): Promise<void> {
  await db.transaction('rw', [db.drafts, db.drawings, db.players, db.teams, db.games, db.alignmentPresets, db.syncTombstones], async () => {
    await Promise.all([
      db.drafts.clear(),
      db.drawings.clear(),
      db.players.clear(),
      db.teams.clear(),
      db.games.clear(),
      db.alignmentPresets.clear(),
      db.syncTombstones.clear(),
    ])
  })
}

export async function loadDraft(): Promise<BoardState | undefined> {
  return (await db.drafts.get('current'))?.board
}

export async function saveDraft(board: BoardState): Promise<void> {
  await db.transaction('rw', [db.drafts, db.syncTombstones], async () => {
    const savedAt = Date.now()
    await db.drafts.put({ key: 'current', board, savedAt })
    await db.drafts.put({ key: board.play ? 'play' : 'whiteboard', board, savedAt })
    if (board.play) await db.syncTombstones.delete('drafts:play')
  })
}

export async function loadModeDraft(mode: DraftMode): Promise<BoardState | undefined> {
  return (await db.drafts.get(mode))?.board
}

export async function openDraft(current: BoardState, next: BoardState): Promise<BoardState> {
  return db.transaction('rw', [db.drafts, db.syncTombstones], async () => {
    const savedAt = Date.now()
    await db.drafts.put({ key: current.play ? 'play' : 'whiteboard', board: current, savedAt })
    await db.drafts.put({ key: next.play ? 'play' : 'whiteboard', board: next, savedAt })
    await db.drafts.put({ key: 'current', board: next, savedAt })
    if (next.play) await db.syncTombstones.delete('drafts:play')
    return next
  })
}

export async function switchDraftMode(current: BoardState, mode: DraftMode, fresh = false): Promise<BoardState> {
  return db.transaction('rw', db.drafts, async () => {
    const savedAt = Date.now()
    await db.drafts.put({ key: current.play ? 'play' : 'whiteboard', board: current, savedAt })
    const stored = fresh ? undefined : await db.drafts.get(mode)
    const target = stored?.board || (mode === 'whiteboard' ? createBoard() : undefined)
    if (!target || !!target.play !== (mode === 'play')) throw new Error('No play draft is available to resume.')
    await db.drafts.put({ key: mode, board: target, savedAt })
    await db.drafts.put({ key: 'current', board: target, savedAt })
    return target
  })
}

export async function deletePlayDraft(): Promise<BoardState> {
  return db.transaction('rw', [db.drafts, db.syncTombstones], async () => {
    const current = (await db.drafts.get('current'))?.board
    const whiteboard = current && !current.play ? current : (await db.drafts.get('whiteboard'))?.board || createBoard()
    await db.drafts.delete('play')
    const savedAt = Date.now()
    await db.drafts.put({ key: 'whiteboard', board: whiteboard, savedAt })
    await db.drafts.put({ key: 'current', board: whiteboard, savedAt })
    await db.syncTombstones.put({ id: 'drafts:play', deletedAt: savedAt })
    return whiteboard
  })
}

export async function saveDrawing(board: BoardState, title: string): Promise<SavedDrawing> {
  const saved = { id: board.id, title, board: { ...board, title }, savedAt: Date.now() }
  await db.transaction('rw', [db.drawings, db.drafts, db.syncTombstones], async () => {
    await db.drawings.put(saved)
    await db.drafts.put({ key: 'current', board: saved.board, savedAt: saved.savedAt })
    await db.drafts.put({ key: board.play ? 'play' : 'whiteboard', board: saved.board, savedAt: saved.savedAt })
    if (board.play) await db.syncTombstones.delete('drafts:play')
  })
  return saved
}

export async function listDrawings(): Promise<SavedDrawing[]> {
  return db.drawings.orderBy('savedAt').reverse().filter(drawing => !drawing.deletedAt).toArray()
}

export async function deleteDrawing(id: string): Promise<void> {
  const now = Date.now()
  await db.drawings.update(id, { deletedAt: now, savedAt: now })
}

export async function listDeletedDrawings(): Promise<SavedDrawing[]> {
  return db.drawings.filter(drawing => !!drawing.deletedAt).toArray()
}

export async function restoreDrawing(id: string): Promise<void> {
  await db.drawings.update(id, { deletedAt: undefined, savedAt: Date.now() })
}

export async function listAlignmentPresets(): Promise<SavedAlignmentPreset[]> {
  return db.alignmentPresets.orderBy('updatedAt').filter(preset => !preset.deletedAt).toArray()
}

export async function saveAlignmentPreset(preset: AlignmentPreset): Promise<SavedAlignmentPreset> {
  if (!preset.label.trim() || preset.positions.length !== 5) throw new Error('A preset needs a name and five player positions.')
  const saved = { ...structuredClone(preset), label: preset.label.trim(), updatedAt: Date.now(), deletedAt: undefined }
  await db.alignmentPresets.put(saved)
  return saved
}

export async function deleteAlignmentPreset(id: string): Promise<void> {
  const existing = await db.alignmentPresets.get(id)
  if (existing) await db.alignmentPresets.put({ ...existing, updatedAt: Date.now(), deletedAt: Date.now() })
}
